/**
 * Dark Mode — Junaid Perfumes
 *
 * Handles:
 * - Reading the initial theme from localStorage (set by the anti-flash
 *   inline script in theme.liquid before the DOM renders)
 * - Toggling between light / dark on button click
 * - Persisting the user's choice in localStorage
 * - Reacting to OS-level preference changes when no manual choice exists
 * - Keeping all toggle buttons in sync (desktop + mobile)
 *
 * Exposes window.DarkMode = { toggle, apply } for external use.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'theme-preference';
  var DARK = 'dark';
  var LIGHT = 'light';
  var TOGGLE_SELECTOR = '[data-dark-mode-toggle]';

  /* -------------------------------------------------------------------------
     Core: apply a theme
     Sets data-theme on <html>, persists to localStorage, syncs all toggles,
     and dispatches a custom event for any other scripts that need to react.
  -------------------------------------------------------------------------- */
  function applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      // localStorage unavailable (private browsing, etc.) — silent fail
    }
    syncToggles(theme);
    document.dispatchEvent(
      new CustomEvent('theme:change', { detail: { theme: theme } })
    );
  }

  /* -------------------------------------------------------------------------
     Toggle between light and dark
  -------------------------------------------------------------------------- */
  function toggle() {
    var current = document.documentElement.getAttribute('data-theme');
    applyTheme(current === DARK ? LIGHT : DARK);
  }

  /* -------------------------------------------------------------------------
     Sync all toggle buttons to reflect the current theme
  -------------------------------------------------------------------------- */
  function syncToggles(theme) {
    var isDark = theme === DARK;
    document.querySelectorAll(TOGGLE_SELECTOR).forEach(function (btn) {
      btn.setAttribute(
        'aria-label',
        isDark ? 'Switch to light mode' : 'Switch to dark mode'
      );
      btn.setAttribute('aria-pressed', isDark ? 'true' : 'false');
    });
  }

  /* -------------------------------------------------------------------------
     DOM ready — wire up click handlers and sync initial state
  -------------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll(TOGGLE_SELECTOR).forEach(function (btn) {
      btn.addEventListener('click', toggle);
    });

    // Sync aria attributes to match the theme that was already applied
    // by the inline anti-flash script in <head>
    var current = document.documentElement.getAttribute('data-theme') || LIGHT;
    syncToggles(current);
  });

  /* -------------------------------------------------------------------------
     React to OS-level preference changes
     Only applies when the user hasn't made a manual choice.
  -------------------------------------------------------------------------- */
  var mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', function (e) {
    var stored;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      stored = null;
    }
    if (!stored) {
      applyTheme(e.matches ? DARK : LIGHT);
    }
  });

  /* -------------------------------------------------------------------------
     Public API
  -------------------------------------------------------------------------- */
  window.DarkMode = {
    toggle: toggle,
    apply: applyTheme
  };
})();
